import { z } from "zod";
import { nameTranslationsField, type NameTranslations } from "./competition/common";
import { paginationQuery, searchParam } from "./pagination";

/**
 * `/organization-types` — NOC, HOST, IF, MEDIA, OTHER: the kind of a
 * responsible organisation. It drives the Register chain (which categories an
 * organisation may issue, which sub-agencies it picks from, and what that
 * picker is called — `sub_agency_label`).
 *
 * A table, not an enum (decision D-1, 2026-10-08): admins rename, translate and
 * add types without a migration. `code` is the stable key code and seeds use;
 * it is stored uppercase.
 */

/**
 * Game Settings lists default to 50 rows: the sets are small (tens of rows) and
 * the admin wants one screen, but the ceiling is still `PAGE_SIZE_MAX`.
 */
export const SETTINGS_PAGE_SIZE_DEFAULT = 50;
export const settingsPaginationQuery = paginationQuery.extend({
	page_size: paginationQuery.shape.page_size.removeDefault().default(SETTINGS_PAGE_SIZE_DEFAULT),
});

/** `GET …/options` never returns more than this — a guard, not a page. */
export const OPTIONS_LIMIT = 500;

export const ORGANIZATION_TYPE_SORT_KEYS = ["sort_order", "code", "name", "created_at"] as const;
export type OrganizationTypeSortKey = (typeof ORGANIZATION_TYPE_SORT_KEYS)[number];

export const listOrganizationTypesQuery = settingsPaginationQuery.extend({
	/** Matches `code` or `name`, case-insensitively. */
	search: searchParam.optional(),
	sort: z.enum(ORGANIZATION_TYPE_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListOrganizationTypesQuery = z.infer<typeof listOrganizationTypesQuery>;

/** One row of `GET /organization-types` (and the detail / write responses). */
export interface OrganizationTypeListItem {
	organization_type_id: string;
	code: string;
	name: string;
	name_translations: NameTranslations;
	/** What the sub-agency picker is called for this type ("National federation"). */
	sub_agency_label: string;
	sub_agency_label_translations: NameTranslations;
	sort_order: number;
	is_active: boolean;
	/** Usage — a type anything references cannot be deleted (deactivate it). */
	responsible_organizations_count: number;
	main_functions_count: number;
	sub_agencies_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /organization-types/options` — active rows only, any signed-in role. */
export interface OrganizationTypeOption {
	organization_type_id: string;
	code: string;
	name: string;
	name_translations: NameTranslations;
	sub_agency_label: string;
	sub_agency_label_translations: NameTranslations;
}

/** The `{ id, code, name }` a category (and later an organisation) carries for its type. */
export interface OrganizationTypeRef {
	id: string;
	code: string;
	name: string;
}

/**
 * The organisation type as embedded in a responsible-organisation or
 * sub-agency row: the ref plus `sub_agency_label` — what the sub-agency picker
 * is called for this type ("National federation", "Division"…).
 */
export interface OrganizationTypeRefWithLabel extends OrganizationTypeRef {
	sub_agency_label: string;
}

// ── Write requests ──────────────────────────────────────────────────────────

export interface OrganizationTypeFormMessages {
	codeRequired: string;
	/** Letters, digits, `-` and `_` only. */
	codeFormat: string;
	nameRequired: string;
	subAgencyLabelRequired: string;
	sortOrderInvalid: string;
}

export const enOrganizationTypeFormMessages: OrganizationTypeFormMessages = {
	codeRequired: "Enter a code",
	codeFormat: "Letters, numbers, - and _ only",
	nameRequired: "Enter a name",
	subAgencyLabelRequired: "Enter what the sub-agency picker is called",
	sortOrderInvalid: "Enter a whole number, 0 or more",
};

/** Trimmed FIRST (bug 5), checked, then stored uppercase — it is a key, not a label. */
export const makeOrganizationTypeCodeField = (m: OrganizationTypeFormMessages = enOrganizationTypeFormMessages) =>
	z
		.string()
		.trim()
		.min(1, m.codeRequired)
		.max(20)
		.regex(/^[A-Za-z0-9_-]+$/, m.codeFormat)
		.transform((v) => v.toUpperCase());
export const organizationTypeCodeField = makeOrganizationTypeCodeField();

export const makeSortOrderField = (message: string) => z.coerce.number({ invalid_type_error: message }).int(message).min(0, message).max(9999, message);

const organizationTypeFields = (m: OrganizationTypeFormMessages) => ({
	code: makeOrganizationTypeCodeField(m),
	name: z.string().trim().min(1, m.nameRequired).max(200),
	name_translations: nameTranslationsField,
	sub_agency_label: z.string().trim().min(1, m.subAgencyLabelRequired).max(100),
	sub_agency_label_translations: nameTranslationsField,
	sort_order: makeSortOrderField(m.sortOrderInvalid),
	is_active: z.boolean(),
});

export function makeOrganizationTypeForm(m: OrganizationTypeFormMessages = enOrganizationTypeFormMessages) {
	return z.object(organizationTypeFields(m));
}
export const organizationTypeForm = makeOrganizationTypeForm();
export type OrganizationTypeForm = z.infer<typeof organizationTypeForm>;

export interface CreateOrganizationTypeBody {
	code: string;
	name: string;
	name_translations?: NameTranslations;
	sub_agency_label: string;
	sub_agency_label_translations?: NameTranslations;
	sort_order?: number;
	is_active?: boolean;
}

/** Omitted leaves a field alone. A translations object is stored whole. */
export interface UpdateOrganizationTypeBody {
	code?: string;
	name?: string;
	name_translations?: NameTranslations;
	sub_agency_label?: string;
	sub_agency_label_translations?: NameTranslations;
	sort_order?: number;
	is_active?: boolean;
	/** Headline for this change in the Activity feed. */
	reason?: string;
}

export const toCreateOrganizationTypeBody = (f: OrganizationTypeForm): CreateOrganizationTypeBody => ({ ...f });

/** Same translations regardless of key order. */
export const sameTranslations = (a: NameTranslations | Record<string, string | undefined>, b: NameTranslations | Record<string, string | undefined>): boolean => {
	const norm = (t: Record<string, string | undefined>) =>
		JSON.stringify(
			Object.entries(t)
				.filter(([, v]) => v !== undefined)
				.sort(([x], [y]) => x.localeCompare(y)),
		);
	return norm(a) === norm(b);
};

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateOrganizationTypeBody(f: OrganizationTypeForm, before: OrganizationTypeListItem): UpdateOrganizationTypeBody {
	const body: UpdateOrganizationTypeBody = {};
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	if (!sameTranslations(f.name_translations, before.name_translations)) body.name_translations = f.name_translations;
	if (f.sub_agency_label !== before.sub_agency_label) body.sub_agency_label = f.sub_agency_label;
	if (!sameTranslations(f.sub_agency_label_translations, before.sub_agency_label_translations)) body.sub_agency_label_translations = f.sub_agency_label_translations;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	return body;
}
